/*
 * Aura: small behaviours for the Aura sections. No dependencies.
 *
 * <aura-cycle> steps an `is-active` class through its [data-cycle-item]
 * children. Used by Wearable (app screens) and Mind State (feedback steps).
 * It also writes --aura-active on itself so CSS can position indicators.
 *
 * Attributes:
 *   interval  ms between steps (default 4000)
 *   autoplay  cycle automatically; otherwise it only follows editor selection
 */
class AuraCycle extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-cycle-item]'));
    this.interval = Number(this.getAttribute('interval')) || 4000;
    this.index = Math.max(
      0,
      this.items.findIndex((item) => item.classList.contains('is-active'))
    );
    this.setActive(this.index);

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.canPlay = this.hasAttribute('autoplay') && this.items.length > 1 && !this.reducedMotion.matches;

    if (this.canPlay) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.visible ? this.start() : this.stop();
      });
      this.observer.observe(this);
    }

    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  setActive(index) {
    if (!this.items.length) return;
    this.index = (index + this.items.length) % this.items.length;
    this.items.forEach((item, i) => {
      const active = i === this.index;
      item.classList.toggle('is-active', active);
      if (item.hasAttribute('data-cycle-hide')) item.setAttribute('aria-hidden', String(!active));
    });
    this.style.setProperty('--aura-active', this.index);
  }

  start() {
    if (!this.canPlay || this.timer || this.editorHold) return;
    this.timer = window.setInterval(() => this.setActive(this.index + 1), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  /* In the theme editor, selecting a block shows it and holds the cycle there. */
  onBlockSelect(event) {
    const item = event.target.closest('[data-cycle-item]');
    const index = this.items.indexOf(item);
    if (index < 0) return;
    this.editorHold = true;
    this.stop();
    this.setActive(index);
  }

  onBlockDeselect() {
    this.editorHold = false;
    if (this.visible) this.start();
  }
}

if (!customElements.get('aura-cycle')) {
  customElements.define('aura-cycle', AuraCycle);
}

/*
 * <aura-cover> crossfades a section over the pinned section before it.
 *
 * The covering section is taller than its card: the extra height is scroll
 * runway. Its card sticks to the top for the length of that runway, directly
 * over the pinned section behind it, so the two dissolve into each other in
 * the same place rather than one sliding across the other.
 *
 * Progress through the runway is published as two numbers: --aura-cover-in on
 * the covering section, --aura-cover-out on the pinned one. The sections
 * decide what to do with them, so this stays layout-agnostic. The ranges
 * overlap, so the incoming card is largely there before the one behind
 * finishes leaving and the viewer never sees black between them.
 *
 * Without JS, or with reduced motion, both default to "finished" in CSS and
 * the sections render as ordinary stacked sections.
 */
class AuraCover extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.previous = this.section?.previousElementSibling;
    this.sticky = this.section?.querySelector('[data-aura-sticky]');

    const pinned = this.previous?.querySelector('[data-aura-pinned]');
    if (!this.section || !this.sticky || !pinned) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    /* Below this width the sections stack normally, so there is nothing to
       drive. Matches the breakpoint in the section's CSS. */
    this.wide = window.matchMedia('(min-width: 750px)');

    this.update = this.update.bind(this);
    this.request = this.request.bind(this);

    this.scroller = document.querySelector('.page-wrapper');
    this.scroller?.addEventListener('scroll', this.request, { passive: true });
    window.addEventListener('scroll', this.request, { passive: true });
    window.addEventListener('resize', this.request, { passive: true });
    this.wide.addEventListener('change', this.request);
    this.reducedMotion.addEventListener('change', this.request);
    this.update();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.request);
    window.removeEventListener('scroll', this.request);
    window.removeEventListener('resize', this.request);
    this.wide?.removeEventListener('change', this.request);
    this.reducedMotion?.removeEventListener('change', this.request);
    cancelAnimationFrame(this.frame);
    this.clear();
  }

  clear() {
    this.section?.style.removeProperty('--aura-cover-in');
    this.previous?.style.removeProperty('--aura-cover-out');
  }

  request() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    if (!this.wide.matches || this.reducedMotion.matches) {
      this.clear();
      return;
    }

    /* The runway is the sticky element's bottom margin: how far it can travel
       inside its parent's content box before it lets go. */
    const parent = this.sticky.parentElement;
    const padding = getComputedStyle(parent);
    const content =
      parent.clientHeight - parseFloat(padding.paddingTop) - parseFloat(padding.paddingBottom);
    const runway = content - this.sticky.offsetHeight;
    if (runway <= 0) {
      this.clear();
      return;
    }

    const clamp = (value) => Math.min(1, Math.max(0, value));
    /* Smoothstep, so the dissolve eases rather than tracking the wheel. */
    const ease = (p) => p * p * (3 - 2 * p);
    /* How far into the runway the card is, 0 when it pins, 1 when it lets go */
    const progress = clamp(-this.section.getBoundingClientRect().top / runway);

    const fadeIn = ease(clamp((progress - 0.04) / 0.5));
    const fadeOut = ease(clamp((progress - 0.16) / 0.52));

    this.section.style.setProperty('--aura-cover-in', fadeIn.toFixed(3));
    this.previous.style.setProperty('--aura-cover-out', fadeOut.toFixed(3));
  }
}

if (!customElements.get('aura-cover')) {
  customElements.define('aura-cover', AuraCover);
}

/*
 * <aura-tabs> switches the panels on the deposit page's details block.
 *
 * Progressive enhancement: the markup is a tablist plus panels, the first
 * panel visible. Without JS every panel after the first stays hidden, so the
 * page still shows its main content. Arrow keys move between tabs, as the
 * tab pattern expects.
 */
class AuraTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = (index + step + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });

    /* In the theme editor, selecting a spec or FAQ block opens its panel. */
    this.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[role="tabpanel"]');
      const index = this.panels.indexOf(panel);
      if (index >= 0) this.select(index);
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      panel.classList.toggle('is-active', i === index);
    });
  }
}

if (!customElements.get('aura-tabs')) {
  customElements.define('aura-tabs', AuraTabs);
}

/*
 * <aura-modal> opens the <dialog> inside it from anything carrying
 * data-aura-modal="<this element's id>".
 *
 * Progressive enhancement: the trigger is a real link to the form this
 * duplicates, so before this script runs, or if it never does, the click
 * still lands somewhere useful. The link is only intercepted once the dialog
 * is known to work.
 *
 * Signup is a full page post, so a submit leaves the page and comes back with
 * the posted form's own id as the fragment (that is what :target keys off in
 * aura.css). If that form is this dialog's, reopen on the message rather than
 * returning someone to a page that looks like nothing happened.
 */
class AuraModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.id || !this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.open = this.open.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onToggle = this.onToggle.bind(this);

    /* Triggers match on this element's id, or on its name. The name is what
       lets a section that cannot know the Waitlist section's generated id,
       Mind State for one, still open this modal. */
    const keys = [this.id, this.getAttribute('name')].filter(Boolean);
    const selector = keys.map((key) => `[data-aura-modal="${CSS.escape(key)}"]`).join(', ');
    this.triggers = Array.from(document.querySelectorAll(selector));
    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.open));
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onToggle);

    this.querySelectorAll('[data-aura-modal-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    if (this.holdsPostedForm()) this.open();
  }

  disconnectedCallback() {
    this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this.open));
    this.unlock();
  }

  /* Did the form in here just post? The fragment is the form's id. */
  holdsPostedForm() {
    const hash = window.location.hash.slice(1);
    if (!hash) return false;
    try {
      return Boolean(this.dialog.querySelector(`#${CSS.escape(hash)}`));
    } catch (error) {
      return false;
    }
  }

  open(event) {
    event?.preventDefault();
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.onToggle();

    /* showModal lands focus on the close button, which says nothing about what
       to do next. Start on the first field instead, or on the message if this
       is the reopen after a post. */
    const target =
      this.dialog.querySelector('.aura-signup__message') ||
      this.dialog.querySelector('input:not([type="hidden"])');
    target?.focus({ preventScroll: true });
  }

  /* A modal <dialog> leaves the page behind it scrollable, so hold it still. */
  onToggle() {
    this.dialog.open ? this.lock() : this.unlock();
  }

  lock() {
    document.documentElement.classList.add('aura-modal-open');
  }

  unlock() {
    document.documentElement.classList.remove('aura-modal-open');
  }

  /* The backdrop is part of the dialog, so a click that lands on the element
     itself rather than the card inside it is a click outside. */
  onDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }
}

if (!customElements.get('aura-modal')) {
  customElements.define('aura-modal', AuraModal);
}

/*
 * <aura-reels> lazy-loads and plays the UGC clips.
 *
 * The <video> elements ship with no src at all, only data-src, so the page
 * costs nothing until a tile is close to the viewport. On approach the source
 * is attached and played; on leaving it pauses. Several tiles play at once,
 * which is the point, and the files are only ever fetched if someone actually
 * scrolls to them.
 *
 * Reduced motion holds every clip on its cover image, and the toggle stops or
 * starts the whole row, because moving content needs a way to stop it.
 */
class AuraReels extends HTMLElement {
  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('video[data-src]'));
    if (!this.videos.length) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.wantsAutoplay = !this.hasAttribute('data-no-autoplay') && !this.reducedMotion.matches;
    this.paused = !this.wantsAutoplay;

    /* rootMargin buys a screen of warning, so a clip is usually ready by the
       time it is actually looked at. */
    this.observer = new IntersectionObserver((entries) => entries.forEach((entry) => this.handle(entry)), {
      rootMargin: '200px 0px',
      threshold: 0.1,
    });
    this.videos.forEach((video) => this.observer.observe(video));

    this.dialog = this.querySelector('[data-reels-dialog]');
    if (this.dialog && typeof this.dialog.showModal === 'function') this.setupPlayer();

  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* Attaching src is what starts the download, so it happens here and not in
     the markup. */
  load(video) {
    if (video.dataset.loaded) return;
    video.dataset.loaded = 'true';
    const source = document.createElement('source');
    source.src = video.dataset.src;
    if (video.dataset.mime) source.type = video.dataset.mime;
    video.appendChild(source);
    video.load();
  }

  handle(entry) {
    const video = entry.target;
    if (!entry.isIntersecting) {
      if (!video.paused) video.pause();
      return;
    }
    this.load(video);
    if (this.paused) return;
    /* Autoplay can still be refused; the cover image stays up if so. */
    video.play().catch(() => {});
  }

  /* The row is muted by design, so tapping a tile is how anyone actually
     hears one. It opens with sound and controls, and the row holds still
     behind it rather than competing. */
  setupPlayer() {
    this.player = this.querySelector('[data-reels-player]');
    this.playerHandle = this.querySelector('[data-reels-handle]');
    this.playerLink = this.querySelector('[data-reels-link]');
    if (!this.player) return;

    this.querySelectorAll('[data-reels-open]').forEach((tile) => {
      tile.addEventListener('click', () => this.openPlayer(tile));
    });
    this.querySelector('[data-reels-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => this.closePlayer());
  }

  openPlayer(tile) {
    const video = tile.querySelector('video[data-src]');
    if (!video) return;

    this.rowWasPlaying = !this.paused;
    this.videos.forEach((item) => item.pause());

    this.player.src = video.dataset.src;
    if (video.poster) this.player.poster = video.poster;
    this.player.muted = false;
    this.player.currentTime = 0;

    const handle = tile.dataset.handle;
    if (this.playerHandle) this.playerHandle.textContent = handle || '';
    const link = tile.dataset.link;
    if (this.playerLink) {
      this.playerLink.hidden = !link;
      if (link) {
        this.playerLink.href = link;
        this.playerLink.textContent = tile.dataset.linkLabel || 'View original post';
      }
    }

    document.documentElement.classList.add('aura-modal-open');
    this.dialog.showModal();
    this.player.play().catch(() => {});
  }

  closePlayer() {
    this.player.pause();
    /* Dropping the src stops the download continuing behind a closed dialog. */
    this.player.removeAttribute('src');
    this.player.load();
    document.documentElement.classList.remove('aura-modal-open');

    if (!this.rowWasPlaying) return;
    this.videos.forEach((video) => {
      const box = video.getBoundingClientRect();
      if (box.bottom > 0 && box.top < window.innerHeight) video.play().catch(() => {});
    });
  }

}

if (!customElements.get('aura-reels')) {
  customElements.define('aura-reels', AuraReels);
}

/* Countdown to a UTC timestamp (data-end, in seconds). Liquid renders the
   first frame; this ticks the [data-unit] spans and adds .is-ended at zero. */
class AuraCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Number(this.dataset.end) * 1000;
    this.values = {};
    this.querySelectorAll('[data-unit]').forEach((el) => (this.values[el.dataset.unit] = el));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
    const parts = {
      d: Math.floor(left / 86400),
      h: Math.floor((left % 86400) / 3600),
      m: Math.floor((left % 3600) / 60),
      s: left % 60,
    };
    for (const key in parts) {
      const text = String(parts[key]).padStart(2, '0');
      if (this.values[key] && this.values[key].textContent !== text) this.values[key].textContent = text;
    }
    if (left === 0) {
      this.classList.add('is-ended');
      clearInterval(this.timer);
    }
  }
}

if (!customElements.get('aura-countdown')) {
  customElements.define('aura-countdown', AuraCountdown);
}

/* SMIL animations (the gradient drifting along the hero wave and the arcs)
   ignore prefers-reduced-motion, so pause them explicitly. */
(function pauseSvgMotion() {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  const apply = () => {
    document.querySelectorAll('svg.aura-svg-motion').forEach((svg) => {
      if (query.matches) {
        svg.pauseAnimations();
        svg.setCurrentTime(0);
      } else {
        svg.unpauseAnimations();
      }
    });
  };
  apply();
  query.addEventListener('change', apply);
  document.addEventListener('shopify:section:load', apply);
})();
